import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import { useMemo } from 'react'
import { GeckoMeasurementRange } from 'types'

/**
 * Hook to get the nominal range string for components
 * @param componentToNominalRange Map of component IDs to their nominal ranges
 * @returns A formatted string representing the nominal range
 */
export const useComponentNominalRangeString = (
  componentToNominalRange: Map<ComponentId, GeckoMeasurementRange> | undefined
): string => {
  const { formatMeasurement } = useRugReportOps()

  return useMemo(() => {
    if (!componentToNominalRange || componentToNominalRange.size === 0) {
      return 'N/A'
    }

    // Get all nominal ranges
    const ranges = Array.from(componentToNominalRange.values())

    // If all ranges are the same, show just one
    const firstRange = ranges[0]
    const allSame = ranges.every(
      (range) =>
        range.min.num === firstRange.min.num &&
        range.max.num === firstRange.max.num &&
        range.min.unit === firstRange.min.unit &&
        range.max.unit === firstRange.max.unit
    )

    if (allSame && firstRange) {
      return `${formatMeasurement({
        measurement: firstRange.min,
        variant: 'narrow',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} - ${formatMeasurement({
        measurement: firstRange.max,
        variant: 'narrow',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })}`
    }

    // Otherwise, find the overall min and max
    let overallMin = ranges[0].min
    let overallMax = ranges[0].max

    ranges.forEach((range) => {
      if (range.min.num < overallMin.num) {
        overallMin = range.min
      }
      if (range.max.num > overallMax.num) {
        overallMax = range.max
      }
    })

    return `${formatMeasurement({
      measurement: overallMin,
      variant: 'narrow',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })} - ${formatMeasurement({
      measurement: overallMax,
      variant: 'narrow',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })}`
  }, [componentToNominalRange, formatMeasurement])
}
